import graphql from "babel-plugin-relay/macro";
import * as React from "react";
import { useLazyLoadQuery } from "react-relay";
import { useNavigate } from "react-router-dom";

import styled from "styled-components";

import AppContainer from "../components/AppContainer";

import MapView, { MapFeature, MapMarker } from "../components/Map";
import MapControls from "../components/Map/MapControls";
import MapListView from "../components/Map/MapListView";
import { Maps } from "../components/Map/MapTileLayer";
import { useFilteredData, usePopulateFilters } from "../hooks/useFilters";
import { getCentroid, getWindowHeight } from "../utils";
import { AuthContext, fetchUserID } from "./Auth";
import {
  InspectionMapQuery as InspectionMapQueryType,
  InspectionfieldStateEnum,
} from "./__generated__/InspectionMapQuery.graphql";
import { createContext, useState } from "react";

export const PlanMultipleContext = createContext<{
  planMultiple?: boolean;
  setPlanMultiple: Function;
}>({ setPlanMultiple: () => {} });

export const InspectionMapContainer = React.memo(function Detail(props: {}) {
  return (
    <AppContainer page="/kaart">
      <React.Suspense fallback={<Fallback />}>
        <FieldMap />
      </React.Suspense>
    </AppContainer>
  );
});

export const InspectionMapQuery = graphql`
  query InspectionMapQuery($userId: ID) {
    user {
      roles {
        edges {
          node {
            collective {
              boundingBox
            }
          }
        }
      }
    }
    allInspectionfields(inspector_Isnull: false, inspector_User: $userId) {
      totalCount
      edgeCount
      ...MapListViewFragment
      edges {
        node {
          state
          id
          member {
            id
            name
          }
          year
          plannedDate
          managementPackageVariant {
            code
          }
          field {
            geometry
          }
        }
      }
    }
  }
`;

function FieldMap() {
  const [, setTransition] = React.useTransition();
  const navigate = useNavigate();
  const [polygonsVisible] = useState(true);
  const [markersVisible] = useState(true);
  const [focused, setFocused] = useState<string>();
  const [selected, setSelected] = useState<string[]>([]);
  const [planMultiple, setPlanMultiple] = useState<boolean>(false);
  const [activeLayer, setActiveLayer] = useState<Maps>(() => {
    const storedLayer = sessionStorage.getItem("activelayer");
    return storedLayer ? (storedLayer as Maps) : Maps.LUFOWINTER;
  });

  const [activeStateMapMarkers, setActiveStateMapMarkers] = useState<
    InspectionfieldStateEnum[]
  >(() => {
    const storedState = JSON.parse(
      window.sessionStorage.getItem("activeStateMapMarkers") || "[]"
    );
    return storedState.length ? storedState : ["ASSIGNED", "PLANNED"];
  });

  const authContext = React.useContext(AuthContext);
  const data = useLazyLoadQuery<InspectionMapQueryType>(InspectionMapQuery, {
    userId: authContext?.user ? fetchUserID(authContext?.user) : null,
  });
  usePopulateFilters(data?.allInspectionfields);
  React.useEffect(() => {
    window.sessionStorage.setItem(
      "activeStateMapMarkers",
      JSON.stringify(activeStateMapMarkers)
    );
  }, [activeStateMapMarkers]);
  const filteredData = useFilteredData(data.allInspectionfields);
  const handleFocus = React.useCallback((id: string) => {
    setTransition(() => {
      setFocused(id);
    });
  }, []);
  const handleClick = React.useCallback(
    (id: string) => {
      setTransition(() => {
        navigate(`/b/${id}`);
      });
    },
    [navigate]
  );
  const handleSelect = React.useCallback((id: string) => {
    setTransition(() => {
      setSelected((currSelected) =>
        currSelected?.includes(id)
          ? currSelected?.filter((cId) => cId !== id) || []
          : [...currSelected, id]
      );
      setFocused(undefined);
    });
  }, []);
  const handleDeselect = React.useCallback((ids: string[]) => {
    setTransition(() => {
      setSelected((currSelected) =>
        currSelected?.filter((cId) => !ids.includes(cId))
      );
      setFocused(undefined);
    });
  }, []);

  const handleClickMarker = React.useCallback(
    (id: string) => {
      setTransition(() => {
        setFocused((currFocus) => {
          const selectedNode = filteredData.find(
            (data) => data?.node?.id === id
          )?.node;
          if (
            currFocus === id &&
            selectedNode?.state === "ASSIGNED" &&
            planMultiple
          ) {
            handleSelect(id);
            return undefined;
          }
          return id;
        });
      });
    },
    [filteredData, handleSelect, planMultiple]
  );
  const visibleInspectionFields = React.useMemo(
    () =>
      filteredData
        ?.filter((e) => e?.node?.field?.geometry)
        ?.filter(
          (e) => e?.node?.state && activeStateMapMarkers.includes(e.node.state)
        ),
    [filteredData, activeStateMapMarkers]
  );
  const geoms = React.useMemo(
    () =>
      visibleInspectionFields
        ?.map((e) => ({
          id: e?.node?.id,
          center: getCentroid(e?.node?.field?.geometry)?.reverse(),
          geometry: e?.node?.field?.geometry,
          state: e?.node?.state as InspectionfieldStateEnum,
        }))
        ?.filter((geom) => !!geom?.geometry),
    [visibleInspectionFields]
  );
  const focusedCenter = React.useMemo(() => {
    return geoms?.find((g) => g?.id === focused)?.center;
  }, [geoms, focused]);
  const markersCenter = React.useMemo(() => {
    const centers = geoms?.map((item) => item.center);
    const totalCoordinates = centers.reduce((acc: number[], cur: number[]) => {
      if (!acc?.length) return [cur[0], cur[1]];
      return [acc[0] + cur[0], acc[1] + cur[1]];
    }, []);
    if (!totalCoordinates?.length) return undefined;
    return [
      totalCoordinates[0] / centers.length,
      totalCoordinates[1] / centers.length,
    ];
  }, [geoms]);
  const permissionsBox = React.useMemo(() => {
    const centers = data?.user?.roles?.edges?.map((e) =>
      e?.node?.collective?.boundingBox?.map((n) => Number(n))
    );

    if (!centers || centers.length === 0) {
      // netherland boundings
      return [3.363889, 50.756069, 7.209444, 53.509299];
    }
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    centers.forEach((cur) => {
      if (!cur?.length) return;
      minX = Math.min(minX, cur[0]);
      minY = Math.min(minY, cur[1]);
      maxX = Math.max(maxX, cur[2]);
      maxY = Math.max(maxY, cur[3]);
    });
    return [minX, minY, maxX, maxY];
  }, [data?.user?.roles?.edges]);
  const handleSetActiveStateMapMarkers = (
    state: InspectionfieldStateEnum,
    active: boolean
  ) => {
    setActiveStateMapMarkers((prevState) =>
      active
        ? [...prevState, state]
        : prevState.filter((marker) => marker !== state)
    );
  };
  return (
    <Container>
      <MapContainer>
        <MapView
          activelayer={activeLayer}
          bbox={permissionsBox}
          initialCenter={markersCenter}
          trackPosition
        >
          <MapControls
            activeStateMapMarkers={activeStateMapMarkers}
            onChangeActiveStateMapMarkers={handleSetActiveStateMapMarkers}
            activeLayer={activeLayer}
            onChangeActiveLayer={(layer) => {
              setActiveLayer(layer);
              sessionStorage.setItem("activelayer", layer);
            }}
          />
          {polygonsVisible &&
            geoms?.map((geom) => (
              <MapFeature
                key={`${geom?.id}_feature`}
                id={geom.id}
                geometry={geom?.geometry}
              />
            ))}
          {markersVisible &&
            geoms?.map((geom) => (
              <MapMarker
                key={`${geom?.id}_marker`}
                id={geom.id}
                point={geom?.center}
                state={geom.state}
                focused={focused === geom.id}
                selected={selected?.includes(geom.id!)}
              />
            ))}
          {data.allInspectionfields ? (
            <PlanMultipleContext.Provider
              value={{ planMultiple, setPlanMultiple }}
            >
              <MapListView
                input={data.allInspectionfields}
                selectedIds={selected}
                focused={focused}
                focusedCenter={focusedCenter}
                onClick={handleClick}
                onClickMarker={handleClickMarker}
                onFocus={handleFocus}
                onSelect={handleSelect}
                onDeselect={handleDeselect}
                ids={visibleInspectionFields?.map((e) => e?.node?.id || "")}
              />
            </PlanMultipleContext.Provider>
          ) : null}
        </MapView>
      </MapContainer>
    </Container>
  );
}

const Fallback = React.memo(function Comp() {
  return (
    <Container>
      <MapContainerFallback />
    </Container>
  );
});

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  min-height: ${getWindowHeight(70)};
`;

export const MapContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(${getWindowHeight()} - 122px);
  width: calc(100vw - 100px);
  @media only screen and (max-width: 768px) {
    width: 100vw;
    padding-right: unset;
  }
  border-radius: 5px;
  overflow: hidden;
  background-color: white;
`;

export const MapContainerFallback = styled(MapContainer)`
  background-color: rgba(229, 229, 229, 0.7);
  animation: background-color-change 3s infinite;
  @keyframes background-color-change {
    0% {
      background-color: rgba(229, 229, 229, 0.7);
    }
    50% {
      background-color: rgba(229, 229, 229, 0.4);
    }
    100% {
      background-color: rgba(229, 229, 229, 0.7);
    }
  }
`;

export default InspectionMapContainer;
